import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { getResults } from './metaAnalysisApi';
import { listProfileJobs } from './profileApi';
import { getDataSourceById, toUiDataSource } from '../DataSources/datasourcesApi';
import AdoptedFromBadge from '../DataSources/AdoptedFromBadge';
import { normalizeSnapshot, columnsForTable, diffColumns } from './snapshotUtils';
import { INK, STATUS, RADIUS } from 'Utils/styleTokens';
import './styles.css';

/**
 * Per-table detail page (/metadata/source/:dataSourceId/table/:tableName).
 * Shows one table's columns with their SQL data type, diffed against the previous
 * profile: added columns are green, removed are red, and a column whose type is
 * unchanged shows "—" in the Previous-type cell rather than repeating the type.
 *
 * Refresh-safe: navigation from the data source page passes the already-loaded current
 * and previous column lists as `location.state` for an instant first paint, but the load
 * re-derives everything from the two most recent successful runs so a direct visit or
 * reload works the same.
 */
const TableDetail = () => {
  const { dataSourceId, tableName: tableParam } = useParams();
  const history = useHistory();
  const location = useLocation();
  const tableName = decodeURIComponent(tableParam);
  const navState = location.state || {};

  const [sourceName, setSourceName] = useState(navState.sourceName || null);
  const [sourceRegisteredServiceId, setSourceRegisteredServiceId] = useState(null);
  const [sourceRegisteredServiceName, setSourceRegisteredServiceName] = useState(null);
  const [current, setCurrent] = useState(
    Array.isArray(navState.current) ? navState.current : null
  );
  // undefined = not yet loaded; null = no previous snapshot exists; array = prior columns.
  const [previous, setPrevious] = useState(
    navState.previous !== undefined ? navState.previous : undefined
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    let cancelled = false;
    try {
      const [dto, jobsPage] = await Promise.all([
        getDataSourceById(dataSourceId).catch(() => null),
        listProfileJobs({ dataSourceId }),
      ]);
      if (cancelled) return;
      if (dto) {
        const ui = toUiDataSource(dto);
        setSourceName(ui.name);
        setSourceRegisteredServiceId(ui.registeredServiceId || null);
        setSourceRegisteredServiceName(ui.registeredServiceName || null);
      }
      const jobs = (jobsPage && jobsPage.content) || (Array.isArray(jobsPage) ? jobsPage : []);
      const succeeded = jobs.filter((j) => j.status === 'SUCCEEDED').slice(0, 2);
      const [curSnap, prevSnap] = await Promise.all(
        succeeded.map(async (j) => {
          try {
            return normalizeSnapshot(j.id, await getResults(j.id));
          } catch (e) {
            return null;
          }
        })
      );
      if (cancelled) return;
      setCurrent(columnsForTable(curSnap, tableName) || []);
      // A second successful run exists → its columns are the comparison baseline; none
      // → null (no previous profile to diff against).
      setPrevious(succeeded.length > 1 ? (columnsForTable(prevSnap, tableName) || []) : null);
      setError(null);
    } catch (e) {
      if (!cancelled) setError((e && e.message) || 'Failed to load table detail');
    }
  }, [dataSourceId, tableName]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const diff = useMemo(
    () => diffColumns(current || [], previous === undefined ? null : previous),
    [current, previous]
  );

  const rowStyle = (status) => {
    if (status === 'added') return { background: 'rgba(21,128,61,0.07)' };
    if (status === 'removed') return { background: 'rgba(185,28,28,0.06)' };
    return undefined;
  };
  const nameStyle = (status) => {
    const base = { fontFamily: 'monospace', fontSize: 12, textAlign: 'left', paddingLeft: 10 };
    if (status === 'added') return { ...base, color: STATUS.success.text, fontWeight: 600 };
    if (status === 'removed') return { ...base, color: STATUS.danger.text, textDecoration: 'line-through' };
    return { ...base, color: INK[900] };
  };

  const hasPrev = diff.hadPrev;

  // Per-column population stats (V23) — present only for profiles taken after the
  // column-stats collector shipped. Provenance qualifies accuracy: EXACT counted the
  // whole table; CATALOG derives from engine statistics; SAMPLE read a bounded first-N
  // slice (percentages approximate, distinct counts are a floor — rendered with a '+').
  const hasStats = diff.rows.some((r) => r.statsProvenance != null);

  // PK / UNIQUE membership (V32) — present only for profiles taken after the key-constraint
  // capture shipped. One short marker per constraint: PK, UQ, or UQ(n) for a composite key;
  // a trailing "~" flags a partial index or a constraint the engine does not enforce.
  const hasKeys = diff.rows.some((r) => Array.isArray(r.keyConstraints) && r.keyConstraints.length > 0);

  const keyLabel = (r) =>
    (r.keyConstraints || [])
      .map((k) => {
        const base = k.kind === 'PRIMARY_KEY' ? 'PK' : 'UQ';
        const comp = k.columnCount > 1 ? `(${k.position}/${k.columnCount})` : '';
        const weak = k.partial || k.enforced === false ? '~' : '';
        return `${base}${comp}${weak}`;
      })
      .join(' ');

  const keyTooltip = (r) =>
    (r.keyConstraints || [])
      .map((k) => {
        const what = k.kind === 'PRIMARY_KEY' ? 'primary key' : 'unique constraint';
        const comp = k.columnCount > 1 ? ` (column ${k.position} of ${k.columnCount})` : '';
        const notes = [k.partial ? 'partial index' : null, k.enforced === false ? 'not enforced' : null]
          .filter(Boolean)
          .join(', ');
        return `${k.name}: ${what}${comp}${notes ? ` — ${notes}` : ''}`;
      })
      .join('\n');

  const populatedLabel = (r) => {
    if (r.sampledRows == null || r.nullCount == null) return '—';
    if (r.sampledRows <= 0) return '—';
    const nonNull = Math.max(0, r.sampledRows - r.nullCount);
    if (nonNull === 0) return r.statsProvenance === 'EXACT' ? 'empty' : 'none seen';
    const pct = (nonNull / r.sampledRows) * 100;
    const shown = pct >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
    return `${shown}%${r.statsProvenance !== 'EXACT' ? ' ~' : ''}`;
  };

  const statsTooltip = (r) => {
    if (r.statsProvenance == null) return 'No stats collected for this column';
    const src = r.statsProvenance === 'EXACT'
      ? 'exact count of the whole table'
      : r.statsProvenance === 'CATALOG'
        ? 'derived from engine statistics'
        : `estimated from a ${r.sampledRows != null ? r.sampledRows.toLocaleString() : ''}-row sample`;
    const nulls = r.nullCount != null ? `${r.nullCount.toLocaleString()} null` : 'null count unknown';
    return `${nulls} of ${r.sampledRows != null ? r.sampledRows.toLocaleString() : '?'} rows (${src})`;
  };

  return (
    <div className='metadata-page'>
      <div className='metadata-toolbar'>
        <span className='mt-title'>TABLE DETAIL</span>
        <span className='mt-sep'>|</span>
        <button
          type='button'
          className='metadata-sortbtn'
          onClick={() => history.push('/metadata/source/' + encodeURIComponent(dataSourceId))}
        >
          ← Back to data source
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#fff' }}>
        {loading && !current ? (
          <div>
            <Skeleton height={26} width={360} />
            <div style={{ marginTop: 12 }}><Skeleton height={240} /></div>
          </div>
        ) : error && !current ? (
          <div className='metadata-empty' style={{ color: STATUS.danger.text }}>{error}</div>
        ) : (
          <>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {tableName}
            </h2>
            <div style={{ marginTop: 6, fontSize: 13, color: INK[500] }}>
              {sourceName ? (
                <>
                  Data source <strong>{sourceName}</strong>
                  <AdoptedFromBadge
                    registeredServiceId={sourceRegisteredServiceId}
                    registeredServiceName={sourceRegisteredServiceName}
                  />
                  {' · '}
                </>
              ) : null}
              {diff.rows.filter((r) => r.status !== 'removed').length} columns
              {hasPrev && (
                <>
                  {' · '}
                  <span style={{ color: STATUS.success.text }}>+{diff.addedCount} added</span>
                  {' · '}
                  <span style={{ color: STATUS.danger.text }}>−{diff.removedCount} removed</span>
                  {diff.changedCount > 0 && <> · {diff.changedCount} type changed</>}
                </>
              )}
            </div>
            {!hasPrev && (
              <div style={{ marginTop: 8, fontSize: 12, color: INK[400] }}>
                No previous profile to compare — showing current columns.
              </div>
            )}

            <div style={{ marginTop: 16, overflow: 'auto', border: `1px solid ${INK[150]}`, borderRadius: RADIUS.md }}>
              <table className='metadata-itbl'>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', paddingLeft: 10 }}>Column</th>
                    <th style={{ textAlign: 'left', paddingLeft: 10 }}>Type</th>
                    {hasKeys && <th style={{ textAlign: 'left', paddingLeft: 10 }}>Key</th>}
                    {hasStats && <th style={{ textAlign: 'left', paddingLeft: 10 }}>Populated</th>}
                    {hasStats && <th style={{ textAlign: 'left', paddingLeft: 10 }}>Distinct</th>}
                    {hasPrev && <th style={{ textAlign: 'left', paddingLeft: 10 }}>Previous type</th>}
                  </tr>
                </thead>
                <tbody>
                  {diff.rows.map((r) => (
                    <tr key={`${r.status}-${r.name}`} style={rowStyle(r.status)}>
                      <td style={nameStyle(r.status)}>
                        {r.status === 'added' && <span title='Added since the previous profile'>+ </span>}
                        {r.status === 'removed' && <span title='Removed since the previous profile'>− </span>}
                        {r.name}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: INK[500], textAlign: 'left', paddingLeft: 10 }}>
                        {r.dataType || '—'}
                      </td>
                      {hasKeys && (
                        <td
                          style={{ fontFamily: 'monospace', fontSize: 12, color: INK[500], textAlign: 'left', paddingLeft: 10 }}
                          title={keyTooltip(r) || undefined}
                          data-testid={`column-key-${r.name}`}
                        >
                          {keyLabel(r) || '—'}
                        </td>
                      )}
                      {hasStats && (
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: INK[500], textAlign: 'left', paddingLeft: 10 }}
                          title={statsTooltip(r)}>
                          {populatedLabel(r)}
                        </td>
                      )}
                      {hasStats && (
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: INK[500], textAlign: 'left', paddingLeft: 10 }}
                          title={statsTooltip(r)}>
                          {r.distinctCount != null
                            ? `${r.distinctCount.toLocaleString()}${r.statsProvenance === 'SAMPLE' ? '+' : ''}`
                            : '—'}
                        </td>
                      )}
                      {hasPrev && (
                        <td style={{
                          fontFamily: 'monospace', fontSize: 12, textAlign: 'left', paddingLeft: 10,
                          color: r.status === 'changed' ? STATUS.warning.text : INK[400],
                        }}>
                          {/* Same type (or nothing to compare) → dash, don't duplicate the value. */}
                          {r.status === 'changed' ? (r.prevType || '—') : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                  {diff.rows.length === 0 && (
                    <tr>
                      <td colSpan={(hasPrev ? 3 : 2) + (hasStats ? 2 : 0) + (hasKeys ? 1 : 0)} style={{ textAlign: 'left', paddingLeft: 10, color: INK[400] }}>
                        No columns recorded for this table.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TableDetail;
